import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DictatedInput from "./DictatedInput.jsx";
import SourceChip from "./section/SourceChip.jsx";

/**
 * AskBar — Ask about the person, answered from her profile.
 *
 * One input, always present, never expanded until she types. Answers are
 * the record's own words with a source on each; nothing is written for her.
 * The caption says so, because the difference between "what her mom said"
 * and "what a model thinks" is the whole product.
 *
 * Text first, mic inside the field (DictatedInput). On a phone the bar is
 * pinned to the bottom of the viewport; on desktop it sits in the flow.
 * Results open in a panel above the bar; Esc or the close button dismisses
 * the panel and the bar stays.
 *
 * Plan H: each hit shows its best sentence or two (`snippet`), with "More"
 * opening the whole record; the section link sits in the meta row. The
 * server only returns records from published sections, and it records the
 * question for the coordinators — the caption says so.
 *
 * Nov 1: no standing caption. What it said — where answers come from, who
 * sees the questions — is said once, in the About sheet (components/
 * AboutSheet.jsx), which the page opens the first time she puts the cursor
 * here (`onFirstUse`) and from the ⓘ. The placeholder is an example
 * question that fits anyone, not "Ask anything".
 */
const SUBJ = { her: ["she", "is"], his: ["he", "is"], their: ["they", "are"] };

export function askExamples(pronoun = "their") {
  const [subj, be] = SUBJ[pronoun] || SUBJ.their;
  return [`What helps when ${subj} ${be} upset?`, `What's ${pronoun} morning like?`];
}

const SECTION_SLUGS = {
  meet: "meet", communicate: "communicate", sensory: "sensory", passions: "passions",
  day: "day", working_on: "working-on", hard_times: "hard-times", health: "health",
  people: "people", community: "community",
};

export default function AskBar({ clientId, clientName, sectionType = null, pronoun = "their", pinned = false, onFirstUse = null }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState(null);      // null = panel closed
  const [asked, setAsked] = useState("");
  const [error, setError] = useState(null);
  const panelRef = useRef(null);
  // One of two examples per visit; both fit anyone (Nov 1).
  const [example] = useState(() => askExamples(pronoun)[Math.random() < 0.5 ? 0 : 1]);

  const placeholder = sectionType
    ? `Ask about ${pronoun} ${SECTION_WORDS[sectionType] || "this"}…`
    : example;

  const inputRef = useRef(null);
  async function submit() {
    inputRef.current?.stop();   // the arrow button: close the mic before sending
    const text = q.trim();
    if (!text || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/clients/${clientId}/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ q: text, section_type: sectionType }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't ask right now.");
      setHits(d.hits || []);
      setAsked(text);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (hits === null) return;
    const onKey = e => { if (e.key === "Escape") setHits(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hits]);

  return (
    <div style={{ ...s.wrap, ...(pinned ? s.pinned : {}) }}>
      {hits !== null && (
        <div ref={panelRef} style={s.panel} role="region" aria-label="Answers">
          <div style={s.panelHead}>
            <span style={s.askedLine}>“{asked}”</span>
            <button type="button" style={s.close} onClick={() => setHits(null)} aria-label="Close answers">×</button>
          </div>
          {hits.length === 0 ? (
            <p style={s.empty}>Nothing in {pronoun} profile says that yet.</p>
          ) : hits.map(h => <Hit key={`${h.kind}-${h.id}`} hit={h} clientId={clientId} />)}
        </div>
      )}
      <div style={s.row}>
        <DictatedInput ref={inputRef} value={q} onChange={setQ} onSubmit={submit}
                       placeholder={placeholder} ariaLabel="Ask a question" disabled={busy}
                       style={{ flex: 1 }} clientId={clientId} onFocus={onFirstUse} />
        <button type="button" onClick={submit} disabled={busy || !q.trim()} aria-label="Ask"
                style={{ ...s.send, ...(busy || !q.trim() ? s.sendOff : {}) }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </button>
      </div>
      {error && <p style={s.error}>{error}</p>}
    </div>
  );
}

function Hit({ hit: h, clientId }) {
  const [open, setOpen] = useState(false);
  const snippet = h.snippet || h.text;
  const hasMore = !!h.text && h.text.trim() !== snippet.trim();
  const slug = SECTION_SLUGS[h.section_type];
  return (
    <div style={s.hit}>
      <p style={s.hitText}>
        {open ? h.text : snippet}
        {hasMore && (
          <button type="button" onClick={() => setOpen(o => !o)} style={s.more}
                  aria-expanded={open} aria-label={open ? "Show less" : "Show more"}>
            {open ? "Less" : "More"}
          </button>
        )}
      </p>
      <div style={s.hitMeta}>
        <span style={s.framing}>{h.framing}</span>
        {h.source && <SourceChip source={h.source} />}
        {slug && (
          <Link to={`/clients/${clientId}/${slug}`} style={s.openLink}>
            Open {h.section_label || "section"} ›
          </Link>
        )}
      </div>
    </div>
  );
}

const SECTION_WORDS = {
  meet: "story", communicate: "communication", sensory: "senses", passions: "passions",
  day: "day", working_on: "goals", hard_times: "hard times", health: "health",
  people: "people", community: "community",
};

const s = {
  wrap:      { display: "flex", flexDirection: "column", gap: 6, background: "var(--color-bg-base)", padding: "var(--space-3) 0 0" },
  pinned:    { position: "sticky", bottom: 0, paddingBottom: "max(var(--space-3), env(safe-area-inset-bottom))", borderTop: "1px solid var(--color-border)", marginTop: "var(--space-4)", zIndex: 5 },
  row:       { display: "flex", alignItems: "center", gap: 8 },
  send:      { width: 40, height: 40, borderRadius: 999, border: "none", background: "var(--color-accent)", color: "var(--color-text-on-accent)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  sendOff:   { background: "var(--color-bg-inset)", color: "var(--color-text-tertiary)", cursor: "default" },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  panel:     { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-3)", display: "flex", flexDirection: "column", gap: "var(--space-2)", maxHeight: "50vh", overflowY: "auto", boxShadow: "0 8px 24px rgba(26,25,21,0.08)" },
  panelHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  askedLine: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", fontStyle: "italic" },
  close:     { background: "none", border: "none", fontSize: 20, lineHeight: 1, color: "var(--color-text-tertiary)", cursor: "pointer", padding: 0 },
  empty:     { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0 },
  hit:       { display: "block", background: "var(--color-bg-base)", borderRadius: "var(--radius-md)", padding: "var(--space-3)", border: "1px solid var(--color-border)" },
  hitText:   { fontSize: "var(--text-base)", lineHeight: 1.5, color: "var(--color-text-primary)", margin: "0 0 6px", whiteSpace: "pre-line" },
  more:      { font: "inherit", fontSize: "var(--text-sm)", marginLeft: 8, padding: 0, border: "none", background: "none", color: "var(--color-accent)", cursor: "pointer" },
  openLink:  { marginLeft: "auto", fontSize: "var(--text-sm)", color: "var(--color-accent)", textDecoration: "none", whiteSpace: "nowrap" },
  hitMeta:   { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  framing:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
};
